import type React from 'react';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronRight, Loader2 } from 'lucide-react';

const WHATSAPP_URL = import.meta.env.VITE_ELITE_WHATSAPP_URL || '#';

interface EliteModalProps {
  onDismiss: () => void;
}

export const EliteModal: React.FC<EliteModalProps> = ({ onDismiss }) => {
  const { t } = useTranslation('elite');
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const clickedRef = useRef(false);

  const linkRef = useRef<HTMLAnchorElement>(null);

  const handleJoinClick = useCallback(() => {
    if (clickedRef.current) return;
    clickedRef.current = true;
    setIsLoading(true);
    linkRef.current?.click();
    timerRef.current = setTimeout(() => {
      onDismiss();
    }, 1500);
  }, [onDismiss]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const perks = [t('perkEarlyAccess'), t('perkRewards'), t('perkBuild')];

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Close button */}
      <div className="flex justify-end p-4">
        <button
          onClick={onDismiss}
          className="flex h-9 w-9 items-center justify-center text-slate-400 transition-colors active:text-slate-500"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex min-h-0 flex-1 animate-[elite-enter_600ms_ease-out_both] flex-col items-center justify-between overflow-y-auto px-8 pb-6">
        {/* Upper content — centers in available space */}
        <div className="flex flex-1 flex-col items-center justify-center">
          {/* Badge */}
          <div className="mb-[4vh] animate-[elite-badge-pulse_2.4s_ease-in-out_800ms_infinite]">
            <img src="/icons/icononly.png" alt="Matchlytics" className="h-10" />
          </div>

          {/* Title */}
          <h1 className="mb-[2vh] text-center text-2xl font-bold tracking-tight text-slate-900">
            {t('title')}
          </h1>

          {/* Description */}
          <p
            className="mb-[3.5vh] max-w-xs text-center text-sm leading-relaxed text-slate-500"
            dangerouslySetInnerHTML={{ __html: t('description') }}
          />

          {/* Perks */}
          <ul className="flex w-full max-w-xs flex-col gap-2.5">
            {perks.map((perk) => (
              <li
                key={perk}
                className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-2.5"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-600">
                  <ChevronRight className="h-3.5 w-3.5" />
                </span>
                <span className="text-sm font-medium text-slate-700">
                  {perk}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTAs — pinned to bottom */}
        <div className="flex w-full max-w-xs shrink-0 flex-col items-center pt-[3vh]">
          <a
            ref={linkRef}
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden"
            aria-hidden="true"
            tabIndex={-1}
          />
          {/* WhatsApp CTA */}
          <button
            onClick={handleJoinClick}
            disabled={isLoading}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#25D366] px-6 py-3.5 text-base font-semibold text-white shadow-lg shadow-[#25D366]/20 transition-all active:scale-[0.97] disabled:opacity-80 disabled:active:scale-100"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            )}
            {isLoading ? t('cta') + '...' : t('cta')}
          </button>

          {/* Dismiss text — hidden on very short screens where the X button suffices */}
          <button
            onClick={onDismiss}
            className="mt-3 hidden text-sm text-slate-400 transition-colors active:text-slate-500 [@media(min-height:580px)]:block"
          >
            {t('dismiss')}
          </button>
        </div>
      </div>
    </div>
  );
};
